import { useCallback, useMemo, useRef, useState } from "react";
import type { EvidenceRecord, ProblemView, TurnView } from "../../contracts/index";
import { ResolveDialog, ReportDialog } from "../editing/ProblemDialogs";
import type { Editing } from "../editing/Editing";
import { AddCopyDialog } from "../copies/AddCopyDialog";
import { MakePrimaryDialog, MatchDecisionDialog, SeparateCopyDialog } from "../copies/CopyDialogs";
import type { CopyAction } from "../copies/Copies";
import { RestoreDialog, RestrictDialog } from "./AccessDialogs";
import { RequestCheckDialog } from "./Checks";
import { MergeCommunicationDialog, RecordDetailsDialog, SplitCommunicationDialog } from "./Details";

export type RecordDialog = "restrict" | "restore" | "report" | "details" | "more" | "split" | "merge" | "check";

export function useMoreCorrections(selectTurn: (idx: number) => void, setDialog: (d: RecordDialog | null) => void) {
  const opener = useRef<HTMLElement | null>(null);
  const [speaker, setSpeaker] = useState<string | null>(null);
  const open = useCallback(
    (turn: TurnView) => {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setSpeaker(turn.speaker_id);
      selectTurn(turn.idx);
      setDialog("more");
    },
    [selectTurn, setDialog],
  );
  // The dialog has no Radix trigger, so focus goes back to More corrections… once the dialog has unmounted.
  const close = useCallback(() => {
    setDialog(null);
    const el = opener.current;
    window.setTimeout(() => el?.focus(), 0);
  }, [setDialog]);
  return useMemo(() => ({ open, close, speaker }), [open, close, speaker]);
}

type MoreCorrections = ReturnType<typeof useMoreCorrections>;

export function RecordDialogs({
  record,
  turns,
  selectedTurn,
  timeMs,
  editing,
  dialog,
  setDialog,
  copyAction,
  setCopyAction,
  resolving,
  setResolving,
  more,
}: {
  record: EvidenceRecord;
  turns: readonly TurnView[];
  selectedTurn: number | null;
  timeMs: number;
  editing: Editing;
  dialog: RecordDialog | null;
  setDialog: (d: RecordDialog | null) => void;
  copyAction: CopyAction | null;
  setCopyAction: (a: CopyAction | null) => void;
  resolving: ProblemView | null;
  setResolving: (p: ProblemView | null) => void;
  more: MoreCorrections;
}) {
  const splitTurn = turns.find((t) => t.idx === selectedTurn);
  const detailsCom =
    record.communications.find((c) => c.ordinal === (splitTurn?.communication ?? 1)) ?? record.communications[0];
  return (
    <>
      {dialog === "restrict" ? (
        <RestrictDialog
          record={record}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === "restore" ? (
        <RestoreDialog
          record={record}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === "report" ? (
        <ReportDialog
          record={record}
          turn={turns.find((t) => t.idx === selectedTurn) ?? null}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === "check" ? (
        <RequestCheckDialog
          record={record}
          initial={
            turns.find((t) => t.idx === selectedTurn) ?? [...turns].reverse().find((t) => t.start_ms <= timeMs) ?? null
          }
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {resolving ? (
        <ResolveDialog
          problem={resolving}
          onClose={() => {
            setResolving(null);
          }}
        />
      ) : null}
      <CopyActionDialogs record={record} copyAction={copyAction} setCopyAction={setCopyAction} />
      {(dialog === "details" || dialog === "more") && detailsCom ? (
        <RecordDetailsDialog
          record={record}
          communication={detailsCom}
          focusSpeaker={dialog === "more" ? more.speaker : null}
          editing={editing}
          onClose={
            dialog === "more"
              ? more.close
              : () => {
                  setDialog(null);
                }
          }
        />
      ) : null}
      {dialog === "split" && splitTurn ? (
        <SplitCommunicationDialog
          record={record}
          turn={splitTurn}
          editing={editing}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === "merge" && detailsCom ? (
        <MergeCommunicationDialog
          record={record}
          communication={detailsCom}
          editing={editing}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
    </>
  );
}

function CopyActionDialogs({
  record,
  copyAction,
  setCopyAction,
}: {
  record: EvidenceRecord;
  copyAction: CopyAction | null;
  setCopyAction: (a: CopyAction | null) => void;
}) {
  return (
    <>
      {copyAction?.kind === "add" ? (
        <AddCopyDialog
          record={record}
          communication={copyAction.communication}
          onClose={() => {
            setCopyAction(null);
          }}
        />
      ) : null}
      {copyAction?.kind === "primary" ? (
        <MakePrimaryDialog
          record={record}
          copy={copyAction.copy}
          onClose={() => {
            setCopyAction(null);
          }}
        />
      ) : null}
      {copyAction?.kind === "separate" ? (
        <SeparateCopyDialog
          record={record}
          copy={copyAction.copy}
          onClose={() => {
            setCopyAction(null);
          }}
        />
      ) : null}
      {copyAction?.kind === "match" ? (
        <MatchDecisionDialog
          record={record}
          communication={copyAction.communication}
          proposal={copyAction.proposal}
          decision={copyAction.decision}
          onClose={() => {
            setCopyAction(null);
          }}
        />
      ) : null}
    </>
  );
}
